// comments in English (fix mobile menu: fixed full-width dropdown + outside-click refs)
import logo from "../../assets/logo.png";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef, useMemo } from "react";
import "./NavbarMenu.css";
import LanguageSelector from "./LanguageSelector";
import { useTranslation } from "react-i18next";
import NewsApi from "../../api/NewsApi";

const NavbarMenu = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [allNews, setAllNews] = useState([]);

  const [activeSection, setActiveSection] = useState("main");
  const location = useLocation();
  const navigate = useNavigate();

  // comments: refs for outside-click
  const headerRef = useRef(null);     // wraps the header
  const dropdownRef = useRef(null);   // wraps the mobile dropdown
  const searchInputRef = useRef(null);

  const { t, i18n } = useTranslation("NavBar");
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const links = [
    { name: t("main"), href: "/", id: "main" },
    { name: t("about"), href: "/aboutus", id: "aboutus" },
    { name: t("Sign In"), href: "/IndividaulsForm", id: "IndividaulsForm" },
    { name: t("services"), href: "/Service", id: "Service" },
    { name: t("news"), href: "/news", id: "news" },
    { name: t("contactus"), href: "/ContactUs", id: "ContactUs" },
    { name: t("faq"), href: "/faq", id: "faq" }
  ];

  useEffect(() => {
    const observerOptions = { root: null, rootMargin: "100px", threshold: 0.5 };
    const observerCallback = (entries) => {
      let mostVisibleSection = activeSection;
      let maxVisibility = 0;
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > maxVisibility) {
          mostVisibleSection = entry.target.id;
          maxVisibility = entry.intersectionRatio;
        }
      });
      if (mostVisibleSection && mostVisibleSection !== activeSection) {
        setActiveSection(mostVisibleSection);
      }
    };
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    links.forEach(({ id }) => {
      const section = document.getElementById(id);
      if (section) observer.observe(section);
    });
    return () => observer.disconnect();
  }, [activeSection, scrollY]); // keep same deps

  const isLinkActive = (href) => location.pathname === href;

  // comments: close menu/search on outside click (consider both header and dropdown)
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      const insideHeader = headerRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideHeader && !insideDropdown) {
        setIsMenuOpen(false);
        setIsSearchOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // comments: prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  // comments: lazy load news when opening search
  useEffect(() => {
    if (!isSearchOpen || allNews.length) return;
    (async () => {
      try {
        const res = await NewsApi.getNewsData();
        setAllNews(res?.data || []);
        setTimeout(() => searchInputRef.current?.focus(), 0);
      } catch (e) {
        console.error("Error loading news for search:", e);
      }
    })();
  }, [isSearchOpen, allNews.length]);

  useEffect(() => {
    if (isSearchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    } else {
      setSearchTerm("");
    }
  }, [isSearchOpen]);

  const normalize = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/[\u064B-\u065F\u0670]/g, "")
      .replace(/\u0640/g, "")
      .replace(/\s+/g, " ")
      .trim();

  const pickText = (val) => {
    if (!val) return "";
    if (typeof val === "string") return val;
    if (typeof val === "object") {
      const lang = i18n.language || "ar";
      const arCands = [val.ar, val.name_ar, val.title_ar, val.description_ar];
      const enCands = [val.en, val.name_en, val.title_en, val.description_en];
      const misc = [val.label, val.value, val.text, val.title, val.name, val.description];
      const ordered = lang === "ar" ? [...arCands, ...enCands, ...misc] : [...enCands, ...arCands, ...misc];
      const first = ordered.find((x) => typeof x === "string" && x.trim());
      return first || "";
    }
    return String(val);
  };

  const filtered = useMemo(() => {
    const term = normalize(searchTerm);
    const list = allNews || [];
    if (!term) return list.slice(0, 10);
    return list
      .filter((item) => {
        const title = pickText(item.name) || pickText(item.title);
        const desc = pickText(item.description);
        const hay = normalize(`${title} ${desc}`);
        return hay.includes(term);
      })
      .slice(0, 20);
  }, [allNews, searchTerm, i18n.language]);

  const goToDetails = (id) => {
    if (!id) return;
    setIsSearchOpen(false);
    setSearchTerm("");
    navigate(`/stats/${id}`); // TODO: adjust route if needed
  };

  const onSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const first = filtered?.[0];
      if (first?.id) goToDetails(first.id);
    }
    if (e.key === "Escape") setIsSearchOpen(false);
  };

  return (
    <>
      {/* Header */}
      <div
        className="fixed top-0 start-0 end-0 main-container flex py-2 justify-around items-center bg-[rgba(255,255,255,0.3)] backdrop-blur-sm z-40 max-w-[1600px] mx-auto border-b border-[#E5F2E8]"
        ref={headerRef}
      >
        {/* Left: Logo + mobile controls */}
        <div className="scale-90 flex justify-between px-2 w-full lg:w-auto lg:justify-start items-center h-[70px] shrink-0 relative z-[13]">
          <Link to={"/#main"}>
            <img className="md:h-[84px] object-contain max-sm:w-16" src={logo} alt="logo" />
          </Link>

          <div className="flex items-center gap-1">
            <span className="block lg:hidden p-2 cursor-pointer">
              <LanguageSelector />
            </span>

            {/* mobile search toggle */}
            <button
              type="button"
              aria-label="search"
              className="block lg:hidden p-2 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation(); // comments: avoid immediate outside-close
                setIsSearchOpen((v) => !v);
              }}
            >
              <img src="/icon/search.svg" alt="search" className="w-5 h-5" />
            </button>

            {/* burger */}
            <button
              type="button"
              aria-label="menu"
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              className="block lg:hidden p-2 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation(); // comments: avoid outside-close race
                setIsMenuOpen((v) => !v);
              }}
            >
              <span className="block w-6 h-[2px] bg-[#0d6b70] mb-[4px]" />
              <span className="block w-6 h-[2px] bg-[#0d6b70] mb-[4px]" />
              <span className="block w-6 h-[2px] bg-[#0d6b70]" />
            </button>
          </div>
        </div>

        {/* Right: nav links + language + desktop search */}
        <div className="flex justify-between lg:w-[49%] relative items-center">
          <div className="hidden lg:flex flex-col items-end scale-90">
            <div className="flex flex-wrap gap-6 items-center w-full relative">
              {links.map((link) => (
                <span className="relative" key={link.name}>
                  <NavLink
                    to={link.href}
                    className={() =>
                      `text-xs rtl:text-sm font-normal ${
                        isLinkActive(link.href)
                          ? "text-[#0d6b70] nav-link-active font-semibold"
                          : "text-[#667680] font-semibold"
                      }`
                    }
                  >
                    {link.name}
                  </NavLink>
                </span>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* desktop search */}
            <button
              type="button"
              aria-label="search"
              className="hidden lg:flex items-center justify-center w-9 h-9 rounded hover:bg-white/60 transition-colors"
              onClick={() => setIsSearchOpen((v) => !v)}
            >
              <img src="/icon/search.svg" alt="search" className="w-5 h-5" />
            </button>
            <div className="desktop">
              <LanguageSelector />
            </div>
          </div>

          {/* search dropdown (desktop & mobile) - stays inside header */}
          {isSearchOpen && (
            <div className="absolute top-[100%] end-0 mt-2 w-[90vw] max-w-[420px] bg-white rounded-lg shadow-lg border border-[#e7e7e7] z-50">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-[#eee]">
                <img src="/icon/search.svg" alt="search" className="w-5 h-5 shrink-0" />
                <input
                  ref={searchInputRef}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={onSearchKeyDown}
                  placeholder={t("search_placeholder", { defaultValue: "بحث عن الأخبار..." })}
                  className="w-full h-10 bg-transparent text-[14px] outline-none"
                />
                <button
                  type="button"
                  className="w-8 h-8 flex items-center justify-center rounded hover:bg-[#f7f7f7]"
                  onClick={() => setIsSearchOpen(false)}
                  aria-label="close"
                  title={t("close", { defaultValue: "إغلاق" })}
                >
                  <span className="block w-4 h-[2px] bg-[#999] rotate-45 translate-y-[1px]" />
                  <span className="block w-4 h-[2px] bg-[#999] -rotate-45 -translate-y-[1px]" />
                </button>
              </div>

              <div className="max-h-80 overflow-auto">
                {filtered?.length ? (
                  filtered.map((n) => {
                    const title = pickText(n.name) || pickText(n.title) || "-";
                    return (
                      <button
                        key={n.id}
                        type="button"
                        onClick={() => goToDetails(n.id)}
                        className="w-full text-start px-4 py-3 hover:bg-[#F7FBFE] focus:bg-[#F1F8FD] transition-colors"
                        title={title}
                      >
                        <div className="text-[14px] text-[#0d6b70] font-medium line-clamp-2">{title}</div>
                      </button>
                    );
                  })
                ) : (
                  <div className="px-4 py-6 text-[13px] text-[#667680]">
                    {t("no_results", { defaultValue: "لا توجد نتائج مطابقة" })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile menu as FIXED full-width dropdown (outside header) */}
      {isMenuOpen && (
        <div
          id="mobile-menu"
          ref={dropdownRef}
          className="fixed top-[70px] inset-x-0 bg-white shadow-lg lg:hidden z-[60] border-t border-[#E5F2E8]"
          // comments: close when a link is clicked
          onClick={(e) => {
            // comments: allow clicks inside, only close on link clicks handled below
          }}
        >
          <ul className="flex flex-col gap-6 p-5">
            {links.map((link) => (
              <li key={link.name}>
                <NavLink
                  to={link.href}
                  className={() =>
                    `block text-sm font-normal ${
                      isLinkActive(link.href)
                        ? "text-[#0d6b70] nav-link-active"
                        : "text-[#667680]"
                    }`
                  }
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};

export default NavbarMenu;
